package com.siriusxm.pia.operations.components

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.getString
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.operations.parseLogTags
import com.siriusxm.unifiedcontent.cmc.CMCService
import com.siriusxm.unifiedcontent.cmc.Log
import com.siriusxm.unifiedcontent.cmc.QueryResponse
import kotlinx.coroutines.launch
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * Configuration for the LogsViewer component.
 *
 * @property client The CMCService client for fetching logs
 * @property query The DataDog query string (e.g., "service:my-service")
 * @property service Optional service filter
 * @property workload Optional workload filter
 * @property region Optional region filter
 * @property environment Optional environment filter
 * @property initialTimeRange Initial time range for the query
 * @property limit Maximum number of logs per page
 * @property showFilters Whether to show filter controls
 */
class LogsViewerConfig {
    var client: CMCService? = null
    var query: String = ""
    var service: String? = null
    var workload: String? = null
    var region: String? = null
    var environment: String? = null
    var initialTimeRange: TimeRange = TimeRange(PeriodType.Hours.periodOf(1))
    var limit: Int = 100
    var showFilters: Boolean = true
    var datadogUrl: String? = null

    internal var onDatadogUrlReceived: ((String) -> Unit)? = null

    /**
     * Callback when a DataDog URL is received from the API response.
     */
    fun onDatadogUrl(callback: (String) -> Unit) {
        onDatadogUrlReceived = callback
    }
}

/**
 * A component for viewing DataDog logs with filtering, pagination, and expandable detail views.
 *
 * Features:
 * - Time range selector with relative and absolute options
 * - Expandable table rows showing full log details
 * - Color-coded severity (error=red, warning=yellow)
 * - Pagination support via "Load More" button
 * - Link to DataDog UI
 *
 * @param config Configuration block for the viewer
 */
@Composable
fun LogsViewer(config: LogsViewerConfig.() -> Unit) {
    val viewerConfig = LogsViewerConfig().apply(config)
    val client = viewerConfig.client ?: return

    var timeRange by remember { mutableStateOf(viewerConfig.initialTimeRange) }
    var logs by remember { mutableStateOf<List<Log>>(emptyList()) }
    var nextToken by remember { mutableStateOf<String?>(null) }
    var datadogUrl by remember { mutableStateOf<String?>(viewerConfig.datadogUrl) }
    var isLoading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    val scope = rememberCoroutineScope()

    // Function to load logs
    suspend fun loadLogs(reset: Boolean = false) {
        isLoading = true
        error = null

        try {
            val times = timeRange.range(Clock.System.now())
            val response: QueryResponse = client.operations.queryLogs(
                query = viewerConfig.query,
                limit = viewerConfig.limit,
                startTs = times.start,
                endTs = times.endInclusive,
                paginationToken = if (reset) null else nextToken,
                flex = null,
                service = viewerConfig.service,
                workload = viewerConfig.workload,
                region = viewerConfig.region,
                environment = viewerConfig.environment
            )

            val newLogs = response.logs ?: emptyList()
            logs = if (reset) newLogs else logs + newLogs
            nextToken = response.nextToken

            // Capture DataDog URL if provided
            response.url?.let { url ->
                datadogUrl = url
                viewerConfig.onDatadogUrlReceived?.invoke(url)
            }
        } catch (e: Exception) {
            error = e.message ?: "Failed to load logs"
        } finally {
            isLoading = false
        }
    }

    // Load logs when time range changes
    LaunchedEffect(timeRange) {
        loadLogs(reset = true)
    }

    Style(OperationsStyles)

    Column(1.cssRem) {
        // Controls row: time range selector, right-aligned
        if (viewerConfig.showFilters) {
            Div({
                classes(OperationsStyles.logsViewerControls)
                style {
                    justifyContent(JustifyContent.FlexEnd)
                    backgroundColor(SXMUI.containerContentBackgroundColor.value())
                    padding(1.cssRem)
                }
            }) {
                Div({
                    classes(OperationsStyles.logsViewerFilters)
                }) {
                    timeRangeSelect(selected = timeRange) { newRange ->
                        if (newRange != null) {
                            timeRange = newRange
                        }
                    }
                }
            }
        }

        // Error message
        error?.let { errorMessage ->
            Div({
                style {
                    padding(10.px)
                    backgroundColor(Color("#ffebee"))
                    color(Color("#c62828"))
                    borderRadius(4.px)
                }
            }) {
                Text(errorMessage)
            }
        }

        // Loading indicator
        if (isLoading && logs.isEmpty()) {
            Div({
                style {
                    padding(20.px)
                    textAlign("center")
                    color(Color("#666"))
                }
            }) {
                Text("Loading logs...")
            }
        }

        // Logs table
        if (logs.isNotEmpty()) {
            table<Log> {
                items(logs)

                column {
                    style {
                        padding(0.px)
                    }
                    content { log ->
                        LogListItem(log)
                    }
                }

                expandable { log ->
                    LogDetailView(log)
                }
            }

            // Load more button
            if (nextToken != null) {
                Div({
                    style {
                        padding(10.px)
                        textAlign("center")
                    }
                }) {
                    button {
                        title = if (isLoading) "Loading..." else "Load More"
                        enabled = !isLoading
                        action {
                            scope.launch {
                                loadLogs(reset = false)
                            }
                        }
                    }
                }
            }
        } else if (!isLoading && error == null) {
            Div({
                style {
                    padding(20.px)
                    textAlign("center")
                    color(Color("#666"))
                }
            }) {
                Text("No logs found for the selected time range")
            }
        }
    }
}

/**
 * Renders a single log item in the table row.
 *
 * Displays:
 * - Context information (subsystem, function name, region)
 * - Log message or error details
 * - Color-coded background based on severity
 *
 * @param log The log entry to display
 */
@Composable
fun LogListItem(log: Log) {
    val status = log.attributes?.getString("status")
    val tags = parseLogTags(log.tags)

    val subsystem = tags["sxm_uni-content_subsystem"]
    val functionName = tags["shortfunctionname"]
    val region = tags["region"]

    val itemStyle = when (status) {
        "error" -> OperationsStyles.logListError
        "warning" -> OperationsStyles.logListWarning
        else -> OperationsStyles.logListInfo
    }

    Div({
        classes(OperationsStyles.logListItem, itemStyle)
    }) {
        val message = log.attributes?.getString("message")
        val error = (log.attributes as? JsonObject)?.get("error") as? JsonObject

        // Context line (subsystem, function, region)
        if (subsystem != null || functionName != null) {
            Div({
                classes(OperationsStyles.logListItemContext)
            }) {
                val resolvedFunctionName = functionName?.removePrefix(subsystem ?: "")?.removePrefix("-")
                Text(listOfNotNull(subsystem, resolvedFunctionName, region).joinToString(" • "))
            }
        }

        // Message content
        Div({
            classes(OperationsStyles.logListItemMessage)
        }) {
            when {
                error != null -> {
                    val errorMessage = error["message"]?.jsonPrimitive?.content
                    if (errorMessage != null) {
                        Text(errorMessage)
                    } else if (message != null) {
                        Text(message)
                    }
                }

                message != null -> {
                    Text(message)
                }
            }
        }
    }
}

/**
 * Renders the expanded detail view for a log entry.
 *
 * Shows tags as inline badges followed by the log attributes in a formatted table.
 *
 * @param log The log entry to display details for
 */
@Composable
fun LogDetailView(log: Log) {
    Div({
        classes(OperationsStyles.logDetailView)
    }) {
        // Show tags as inline badges
        if (!log.tags.isNullOrEmpty()) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexWrap(FlexWrap.Wrap)
                    gap(4.px)
                    marginBottom(12.px)
                }
            }) {
                log.tags?.forEach { tag ->
                    Badge(tag, BadgeColor.GRAY, fontSize = 10.px)
                }
            }
        }

        // Show attributes as a table
        (log.attributes as? JsonObject)?.let { attrs ->
            jsonTable(attrs, hideEmptyOrNull = true)
        }
    }
}
